import type { QueryClient } from "@tanstack/react-query";
import type { ChildMeta, EntityMeta } from "@wms/domain";

/**
 * CW31-B11: the cached list of a child tab of this record — `[child, "list", {…, baseFilter: {field: fk, value: id}}]`
 * as EntityTable keys it. Lists of other records and the other lists of the same entity are not matched.
 */
export function isChildListKey(queryKey: readonly unknown[], children: Pick<ChildMeta, "entity" | "fk">[], id: string): boolean {
  const [entity, kind, params] = queryKey;
  if (kind !== "list" || typeof params !== "object" || params === null) return false;
  const filter = (params as { baseFilter?: { field?: unknown; value?: unknown } }).baseFilter;
  return children.some((c) => c.entity === entity && filter?.field === c.fk && String(filter?.value ?? "") === id);
}

/**
 * CW31-B11: after an action of a card («Зафиксировать оплату») the lists of its child tabs («Оплаты») are fetched
 * again at once — only those of this record, without a reload of the page or a reset of the whole cache. The
 * children come from the meta of the card (already cached by the form).
 */
export function refreshChildLists(queryClient: QueryClient, entity: string, id: string, children?: Pick<ChildMeta, "entity" | "fk">[] | null): Promise<void> {
  const list = children ?? queryClient.getQueryData<EntityMeta>(["meta", entity])?.children ?? [];
  if (!list.length || !id || id === "new") return Promise.resolve();
  return queryClient.invalidateQueries({ predicate: (query) => isChildListKey(query.queryKey, list, id) });
}
